import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, MessageSquareText, Save } from "lucide-react";
import { air } from "./api";
import { FaqEditor } from "./FaqEditor";

const TXT = [["hours", "Opening hours", "w-44"], ["today_note", "Today's note", "w-36"], ["delivery_note", "Delivery note", "w-64"], ["parking", "Parking", "w-52"], ["booking_link", "Booking link", "w-48"], ["maps_link", "Maps link", "w-40"], ["menu_link", "Menu link", "w-40"]];
const YN = [["delivery", "Delivery"], ["bookings_via_website", "Bookings on website"], ["table_booking", "Table booking"], ["takeaway", "Takeaway"], ["breakfast", "Breakfast"], ["catering", "Catering"], ["corporate", "Corporate"], ["celebrations", "Celebrations"]];
const ynVal = (v) => (v === true ? "yes" : v === false ? "no" : "");

export function CentreAnswersPanel({ token }) {
  const [rows, setRows] = useState(null);
  const [dirty, setDirty] = useState({});
  const [faqFor, setFaqFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => air("answers/list", token).then((r) => { setRows(r.rows); setDirty({}); }).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rows) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
  const patch = (code, p) => { setRows(rows.map((r) => (r.center === code ? { ...r, ...p } : r))); setDirty({ ...dirty, [code]: true }); };
  const setK = (r, k, v) => patch(r.center, { knowledge: { ...r.knowledge, [k]: v } });
  const save = async () => {
    setSaving(true);
    try { const r = await air("answers/save", token, { rows: rows.filter((x) => dirty[x.center]) }); toast.success(`Saved ${r.saved} centre(s)`); load(); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const cur = rows.find((r) => r.center === faqFor);
  return (
    <div className="space-y-2" data-testid="air-answers-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Facts the AI may tell callers. Blank / "Not confirmed" means the AI offers a callback instead of answering.</p>
        <Button size="sm" onClick={save} disabled={saving || !Object.keys(dirty).length} data-testid="air-answers-save">
          {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}Save all ({Object.keys(dirty).length})</Button>
      </div>
      <div className="overflow-x-auto rounded border">
        <table className="text-xs">
          <thead className="bg-muted/50 text-left"><tr>
            <th className="p-2 sticky left-0 bg-muted">Centre</th>
            {TXT.map(([k, l]) => <th key={k} className="p-2 whitespace-nowrap">{l}</th>)}
            {YN.map(([k, l]) => <th key={k} className="p-2 whitespace-nowrap">{l}</th>)}
            <th className="p-2">Approved answers</th>
          </tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.center} className="border-t align-top" data-testid={`air-answers-row-${r.center}`}>
              <td className="p-2 sticky left-0 bg-background whitespace-nowrap"><p className="font-medium">{r.center}{dirty[r.center] && <span className="ml-1 text-amber-600">•</span>}</p><p className="text-muted-foreground">{r.country}</p></td>
              {TXT.map(([k, , w]) => <td key={k} className="p-1"><Input className={`h-8 ${w}`} value={r.knowledge[k] || ""} onChange={(e) => setK(r, k, e.target.value)} data-testid={`air-ans-${r.center}-${k}`} /></td>)}
              {YN.map(([k]) => <td key={k} className="p-1"><select className="h-8 rounded border" value={ynVal(r.knowledge[k])} onChange={(e) => setK(r, k, e.target.value === "" ? null : e.target.value === "yes")} data-testid={`air-ans-${r.center}-${k}`}><option value="">Not confirmed</option><option value="yes">Yes</option><option value="no">No</option></select></td>)}
              <td className="p-1"><Button size="sm" variant="outline" className="h-8 whitespace-nowrap" onClick={() => setFaqFor(r.center)} data-testid={`air-ans-${r.center}-faq`}>
                <MessageSquareText className="mr-1 h-3 w-3" />{r.approved_responses.filter((f) => f.approved !== false).length} approved
                {r.approved_responses.some((f) => f.approved === false) && <Badge className="ml-1 bg-amber-100 text-amber-800">{r.approved_responses.filter((f) => f.approved === false).length} draft</Badge>}</Button></td>
            </tr>))}</tbody>
        </table>
      </div>
      {cur && (
        <Dialog open onOpenChange={(o) => !o && setFaqFor(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="air-answers-faq-dialog">
            <DialogHeader><DialogTitle>Approved answers — {cur.center}</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">Drafts are suggestions — tick Approved only after checking them. Click "Save all" after closing.</p>
            <FaqEditor faq={cur.approved_responses} onChange={(f) => patch(cur.center, { approved_responses: f })} prefix="air-ans-faq" />
          </DialogContent>
        </Dialog>)}
    </div>
  );
}
